import React, { useState, useEffect, useMemo, forwardRef } from 'react';
import MaterialTable from 'material-table';
import { ThemeProvider, createTheme, Paper, Grid ,Typography} from '@mui/material';
import {
    AddBox,
    Check,
    ChevronLeft,
    ChevronRight,
    Clear,
    DeleteOutline,
    Edit,
    FilterList,
    FirstPage,
    LastPage,
    Remove,
    SaveAlt,
    Search,
    ViewColumn,
    
} from '@mui/icons-material';
import moment from 'moment';

import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL } from 'assets/constants/Constants';
import {
    CALENDER_ICON, HandymanIcon, AddCircleIcon
} from 'assets/constants/Icons';
import Spinner from 'components/Spinner';
import ShowToast from 'components/ShowToast';
import { hasPermission } from 'components/Permission';
import {
    getPartList,
    createParts,
    updatePart,
    deletePart,
    createHistoryList,
    getStdProcessList
} from 'lib/axiosUtils';
import { LOG_TEXT, MODULE_NAMES } from 'assets/constants/Constants';

const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const ComponentPartsPage = (props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [partId, setPartId] = useState(null);
    const [partList, setPartList] = useState([]);
    const [error, setError] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [stdProcessList, setStdProcessList] = useState([]);
    const [partData, setPartData] = useState({
        part_name: '',
        document_number: '',
        part_type: '',
        stock_quantity: '',
        steel_material_description: '',
        od_mm: '',
        length_mm: '',
        required_standerd_processes: '',
        required_steel_material: '',
    });

    const theme = useMemo(() => createTheme(), []);

    useEffect(() => {
        fetchPartList();
        fetchProcessList();
    }, []);

    const fetchProcessList = async () => {
        try {
            const response = await getStdProcessList({}, {});
            setStdProcessList(response?.data?.result || []);
        } catch (err) {
            console.error('Failed to fetch processes:', err);
        }
    };

    const fetchPartList = async () => {
        setIsLoading(true);
        try {
            const response = await getPartList({}, {});
            setPartList(response?.data?.result || []);
        } catch (err) {
            console.error('Failed to fetch parts:', err);
            ShowToast('Could not load parts list.', TYPE_FAIL, INTERVAL, props, '');
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = () => {
        let isValid = true;
        let errorData = {};
        if (!partData.part_name.trim()) {
            isValid = false;
            errorData.part_name = 'Please enter Part Name';
        }
        if (!partData.document_number.trim()) {
            isValid = false;
            errorData.document_number = 'Please enter Document Number';
        }
        if (!partData.part_type) {
            isValid = false;
            errorData.part_type = 'Please select Part Type';
        }
        if (partData.part_type === 'Raw Material') {
            if (!partData.steel_material_description) {
                isValid = false;
                errorData.steel_material_description = 'Please enter Steel Material Description';
            }
            if (!String(partData.od_mm).trim()) {
                isValid = false;
                errorData.od_mm = 'Please enter Outer Diameter (mm)';
            }
            if (!String(partData.length_mm).trim()) {
                isValid = false;
                errorData.length_mm = 'Please enter Length (mm)';
            }
            if (!String(partData.stock_quantity).trim()) {
                isValid = false;
                errorData.stock_quantity = 'Please enter Stock Quantity';
            }
        }
        setError(errorData);
        return isValid;
    };

    const isSuccess = (response) => {
        return (
            response &&
            (response.status === 200 ||
                response.statusCode === 200 ||
                response.status === 201 ||
                response.statusCode === 201)
        );
    };

    const resetForm = () => {
        setPartId(null);
        setPartData({
            part_name: '',
            document_number: '',
            part_type: '',
            stock_quantity: '',
            steel_material_description: '',
            od_mm: '',
            length_mm: '',
            required_standerd_processes: '',
            required_steel_material: '',
        });
        setError({});
    };

    const handleOpenAddModal = () => {
        resetForm();
        setOpenModal(true);
    };

    const handleSavePart = async () => {
        if (!isFormValid()) {
            ShowToast('Please fill all required fields.', TYPE_FAIL, INTERVAL, props, '');
            return;
        } 
            

        setIsLoading(true);
        const requestData = { ...partData };
        delete requestData._id;
        delete requestData.__v;
        let response = null;
        let userData = JSON.parse(localStorage.getItem('userData'));
        let userID = userData?._id;

        try {
            if (partId === null) {
                response = await createParts({}, requestData);
                if (isSuccess(response)) {
                    const newPart = response?.data?.result;
                    if (userID && newPart?._id) {
                        const auditData = {
                            logText: LOG_TEXT.PART_ADDED,
                            userID,
                            moduleName: MODULE_NAMES.COMPONENT_PARTS,
                            doc_id: newPart._id,
                        };
                        await createHistoryList({}, auditData);
                    }
                    ShowToast('Part created successfully', TYPE_SUCCESS, INTERVAL, props, '');
                }
            } else {
                response = await updatePart({}, requestData, partId);
                if (isSuccess(response)) {
                    ShowToast('Part updated successfully', TYPE_SUCCESS, INTERVAL, props, '');
                }
            }

            if (isSuccess(response)) {
                setOpenModal(false);
                resetForm();
                await fetchPartList();
            } else {
                throw new Error('API request failed');
            }
        } catch (err) {
            console.error('Operation failed:', err);
            ShowToast(
                err?.response?.data?.error || 'Operation failed',
                TYPE_FAIL,
                INTERVAL,
                props,
                ''
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (item) => {
        setPartId(item._id);
        setPartData({ ...item });
        setOpenModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPartData({ ...partData, [name]: value });
    };

    const handleDelete = async (item) => {
        if (window.confirm('Are you sure you want to delete this Part?')) {
            setIsLoading(true);
            try {
                const response = await deletePart({}, {}, item._id);
                if (response.statusCode === 204) {
                    await fetchPartList();
                    resetForm();
                    ShowToast('Part deleted successfully', TYPE_SUCCESS, INTERVAL, props, '');
                } else {
                    throw new Error('API delete request failed');
                }
            } catch (err) {
                console.error('Delete failed:', err);
                ShowToast(
                    'Something went wrong. Please try again.',
                    TYPE_FAIL,
                    INTERVAL,
                    props,
                    ''
                );
            } finally {
                setIsLoading(false);
            }
        }
    };

    const standardColumns = [
        { title: 'Part Name', field: 'part_name' },
        { title: 'Document Number', field: 'document_number' },
        { 
            title: 'Req. Process', 
            field: 'required_standerd_processes',
            render: rowData => {
                const proc = stdProcessList.find(p => p._id === rowData.required_standerd_processes);
                return proc ? proc.process_title : '—';
            }
        },
        { title: 'Req. Material', field: 'required_steel_material' },
        { title: 'OD (mm)', field: 'od_mm', type: 'numeric', render: rowData => (
            <div style={{ paddingLeft: '10px' }}>
                {rowData.od_mm}
            </div>),
        },
        { title: 'Length (mm)', field: 'length_mm', type: 'numeric', render: rowData => (
            <div style={{ paddingLeft: '10px' }}>
                {rowData.length_mm}
            </div>),
        },
        {
            title: "Searchable",
            field: "searchableCombined",
            hidden: true,
            searchable: true,
        },
        { title: 'Stock Quantity', field: 'stock_quantity', type: 'numeric' },

    ];

    const rawMaterialColumns = [
        { title: 'Part Name', field: 'part_name' },
        { title: 'Document Number', field: 'document_number' },
        { title: 'Steel Material', field: 'steel_material_description' },
        { title: 'OD (mm)', field: 'od_mm', type: 'numeric', render: rowData => (
            <div style={{ paddingLeft: '10px' }}>
                {rowData.od_mm}
            </div>),
        },
        { title: 'Length (mm)', field: 'length_mm', type: 'numeric', render: rowData => (
            <div style={{ paddingLeft: '10px' }}>
                {rowData.length_mm}
            </div>),
        },
        { title: 'Stock Quantity', field: 'stock_quantity', type: 'numeric' },
        {
            title: "Searchable",
            field: "searchableCombined",
            hidden: true,
            searchable: true,
        },
    ];
    
    const tableActions = [
        hasPermission('parts_page', 'update') ? {
            icon: () => <Edit />,
            tooltip: 'Edit Part',
            onClick: (event, rowData) => handleEdit(rowData),
        } : null,
        hasPermission('parts_page', 'delete') ? {
            icon: () => <DeleteOutline />,
            tooltip: 'Delete Part',
            onClick: (event, rowData) => handleDelete(rowData),
        } : null,
    ].filter(Boolean);

    const tableOptions = {
        actionsColumnIndex: -1,
        headerStyle: {
            color: '#2d3d42',
            fontWeight: 'bolder',
            fontSize: 14,
            textAlign: 'center',
        },
        cellStyle: {
            textAlign: 'center',
            fontSize: 12,
            color: '#2d3d42',
        },
        sorting: false,
        columnsButton: true,
        exportButton: true,
        pageSize: 5,
        pageSizeOptions: [5, 10, 20, 50], 
        toolbar: true,
        search: true, 
    };

    const standardParts = partList.filter((p) => p.part_type === 'Standard');
    const rawMaterialParts = partList.filter((p) => p.part_type === 'Raw Material');

    return (
        <div className="main-content">
            <div className="pagetitle row" style={{ color: 'black', paddingTop: 15, marginTop: "-25px" }}>
                <div className="col-md-10 sm_device">
                    <HandymanIcon aria-hidden="true" />
                    <span style={{ marginLeft: '7px', marginRight: '10px' }}>Parts</span>
                    <AddCircleIcon onClick={handleOpenAddModal}
                        style={{ 
                            cursor: 'pointer', 
                            color: '#1976d2', 
                            fontSize: '30px !important',
                        }}
                        titleAccess="Add Part"
                    />
                </div>
                <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                    <i className={`${CALENDER_ICON}==`}></i> {moment().format('DD MMM YYYY')}
                </div>
            </div>
            <div
                style={{
                    borderBottom: '1px solid lightgray',
                    marginBottom: 15,
                    marginTop: 15,
                }}
            />

            <div className="list-wrapper">
                {/* {hasPermission('parts_page', 'create') && (
                    <div className="form-button-div">
                       <button className="brand-button role-button" onClick={handleOpenAddModal}>
                            Add Part
                        </button>
                    </div>
                )} */}
                {isLoading ? (
                    <Spinner />
                ) : (
                    <div
                        className="mt-4 border list-table-div project-list-table-div bom-table-div"
                        style={{ marginBottom: 50 }}
                    >
                        <ThemeProvider theme={theme}>
                            <Grid container>
                                <Grid item xs={12}  sx={{ margin:  { xs: '0px', sm: '20px', md: '40px'} }}>
                                <Typography variant="title" fontWeight="bold" gutterBottom>
                                Standard Parts:
                                </Typography>          
                                <MaterialTable
                                        columns={standardColumns}
                                        data={standardParts}
                                        icons={tableIcons}
                                        actions={tableActions}
                                        components={{ Container: (props) => <Paper {...props} elevation={2} /> }}
                                        options={{
                                            ...tableOptions,
                                            exportFileName: 'Standard_Parts_List'
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{
                                    margin: { xs: '0px', sm: '20px', md: '40px' },
                                    marginTop: { xs: '40px', sm: '10px', md: '0px' }
                                   }}
                                >
                                <Typography variant="title" fontWeight="bold" gutterBottom>
                                Raw Materials:
                                </Typography>
                                    <MaterialTable
                                        columns={rawMaterialColumns}
                                        data={rawMaterialParts}
                                        icons={tableIcons}
                                        actions={tableActions}
                                        components={{ Container: (props) => <Paper {...props} elevation={2} /> }}
                                        options={{
                                            ...tableOptions,
                                            exportFileName: 'Raw_Materials_List'
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </ThemeProvider>
                    </div>
                )}

                {openModal && (
                    <div className="custom_modal" onClick={() => setOpenModal(false)}>
                        <div className="modal_body" style={{ fontSize: '12px' }} onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="close text-right"
                                style={{ fontSize: '2.5rem' }}
                                onClick={() => setOpenModal(false)}
                            >
                                &times;
                            </button>

                            <h4>{partId ? 'Update Part' : 'Add New Part'}</h4>
                            <hr />

                            {/* --- Row 2: Part Type & Conditional Stock --- */}
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label>Part Type</label>
                                    <select
                                        name="part_type"
                                        className="form-control-customer"
                                        value={partData.part_type}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="Raw Material">Raw Material</option>
                                        <option value="Standard">Standard</option>
                                    </select>
                                    <div className="text-danger">{error.part_type}</div>
                                </div>
                            </div>
                            {/* --- Row 1: Part Name & Document Number --- */}
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label>Part Name</label>
                                    <input
                                        name="part_name"
                                        className="form-control-customer"
                                        value={partData.part_name}
                                        onChange={handleInputChange}
                                    />
                                    <div className="text-danger">{error.part_name}</div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Document Number</label>
                                    <input
                                        name="document_number"
                                        className="form-control-customer"
                                        value={partData.document_number}
                                        onChange={handleInputChange}
                                    />
                                    <div className="text-danger">{error.document_number}</div>
                                </div>
                            </div>

                            {/* --- Conditional Fields for 'Raw Material' --- */}
                            {partData.part_type === 'Raw Material' && (

                                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                        <div style={{ flex: 1 }}>
                                            <label>Steel Material Description</label>
                                            <input
                                                name="steel_material_description"
                                                className="form-control-customer"
                                                value={partData.steel_material_description}
                                                onChange={handleInputChange}
                                            />
                                            <div className="text-danger">{error.steel_material_description}</div>
                                        </div>
                                           
                                    </div>
                                    )
                                }
                                    
                                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                        <div style={{ flex: 1 }}>
                                            <label>Outer Diameter (mm)</label>
                                            <input
                                                name="od_mm"
                                                type="number"
                                                className="form-control-customer"
                                                value={partData.od_mm}
                                                onChange={handleInputChange}
                                            />
                                            <div className="text-danger">{error.od_mm}</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label>Length (mm)</label>
                                            <input
                                                name="length_mm"
                                                type="number"
                                                className="form-control-customer"
                                                value={partData.length_mm}
                                                onChange={handleInputChange}
                                            />
                                            <div className="text-danger">{error.length_mm}</div>
                                        </div>
                                        {
                                            <div style={{ flex: 1 }}>
                                                <label>Stock Quantity</label>
                                                <input
                                                    name="stock_quantity"
                                                    type="number"
                                                    className="form-control-customer"
                                                    value={partData.stock_quantity}
                                                    onChange={handleInputChange}
                                                />
                                                <div className="text-danger">{error.stock_quantity}</div>
                                            </div>
                                        }
                                    </div>
                            

                            {partData.part_type === 'Standard' && (
                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <label>Required Std Process</label>
                                        <select
                                            name="required_standerd_processes"
                                            className="form-control-customer"
                                            value={partData.required_standerd_processes || ''}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select Process</option>
                                            {stdProcessList.map((proc) => (
                                                <option key={proc._id} value={proc._id}>
                                                    {proc.process_title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label>Required Steel Material</label>
                                        <input
                                            name="required_steel_material"
                                            className="form-control-customer"
                                            value={partData.required_steel_material || ''}
                                            onChange={handleInputChange}
                                            placeholder="e.g. EN353"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="mt-3 text-center">
                                <button className="brand-button" onClick={handleSavePart}>
                                    {partId ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComponentPartsPage;